// ** Third Party Import
import { useTranslation } from 'react-i18next';

// ** MUI Components
import { styled } from '@mui/material/styles';

// ** Custom Components
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';

// ** Type Import
import type { LayoutProps } from '../../../../types';
import Box, { type BoxProps } from '@mui/material/Box';

interface Props {
  navHover: boolean;
  navWidth: number;
  collapsedNavWidth: number;
  hidden: LayoutProps['hidden'];
  navigationBorderWidth: number;
  toggleNavVisibility: () => void;
  settings: LayoutProps['settings'];
  saveSettings: LayoutProps['saveSettings'];
  navMenuBranding?: LayoutProps['verticalLayoutProps']['navMenu']['branding'];
}

// ** Styled Components
const MenuHeaderWrapper = styled(Box)<BoxProps>({
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
});

const VerticalNavHeader = (props: Props) => {
  'use memo';

  // ** Props
  const {
    hidden,
    navWidth,
    settings,
    saveSettings,
    toggleNavVisibility,
    navMenuBranding: userNavMenuBranding,
  } = props;

  // ** Hooks & Vars
  const { navCollapsed } = settings;
  const { t } = useTranslation('site-template-ui');

  return (
    <MenuHeaderWrapper className="nav-header">
      <Box>{userNavMenuBranding ? userNavMenuBranding(props) : null}</Box>
      <Box
        className="nav-header-button-container"
        sx={{
          position: 'absolute',
          display: 'flex',
          left: 0,
          top: 0,
          bottom: 0,
          width: navWidth,
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingRight: 1,
        }}
      >
        {hidden ? (
          <PowerIconButton
            icon="xmark"
            title={t('navHeaderCloseSidebar')}
            size="small"
            onClick={toggleNavVisibility}
            sx={{
              p: 3,
              opacity: 0.4,
              transition: 'opacity .25s ease-in-out',
              '&:hover': {
                opacity: 1,
              },
            }}
          />
        ) : (
          <PowerIconButton
            icon={navCollapsed ? 'thumbtack-slash' : 'thumbtack'}
            hoverIcon={navCollapsed ? 'thumbtack' : 'thumbtack-slash'}
            title={navCollapsed ? 'Pin sidebar' : 'Unpin sidebar'}
            size="small"
            onClick={() => saveSettings({ ...settings, navCollapsed: !navCollapsed })}
            sx={{
              p: 3,
              opacity: 0.4,
              transition: 'opacity .25s ease-in-out',
              '&:hover': {
                opacity: 1,
              },
            }}
          />
        )}
      </Box>
    </MenuHeaderWrapper>
  );
};

export default VerticalNavHeader;
